import { NPSAreasEnum } from '../models/nps-areas.enum';

export interface ConditionalQuestionOptionLike {
  id: string;
  value?: string;
}

export interface ConditionalQuestionCardLike {
  id: string;
}

export interface ConditionalQuestionLike {
  id: string;
  options?: ConditionalQuestionOptionLike[];
  ranks?: ConditionalQuestionOptionLike[];
}

export interface AffectedQuestionOption {
  questionId: string;
  optionId: string;
}

export interface AffectedQuestionCard {
  questionId: string;
  cardId: string;
}

export type ConditionalCommand = 'AND' | 'OR';
export type ScalarConditionValue = string | number;

export interface ConditionalCondition {
  questionId: string;
  operator: string;
  cardId: string | null;
  valueToMatch: string[];
  group: number;
  optionId: string | null;
}

export type ConditionGroup = (ConditionalCondition | ConditionalCommand)[];
export interface ChoiceTypeResultLike {
  value: ScalarConditionValue | string[];
  otherText?: string;
}
export type CarouselTypeResultLike = Record<string, ChoiceTypeResultLike>;
export type ParsedConditionalAnswerValue = ScalarConditionValue | string[] | ChoiceTypeResultLike | CarouselTypeResultLike | undefined;

export interface SurveyConditionalEvaluationEnvironment {
  answers: Record<string, unknown>;
  questions: Record<string, ConditionalQuestionLike>;
  parsedAnswers?: Record<string, ParsedConditionalAnswerValue>;
  parseAnswerValue?: (value: unknown) => unknown;
}

export interface ResolvedConditionMatchValues {
  normalizedOptionId: string | undefined;
  normalizedValueToMatch: string[];
  plainValueToMatch: string;
}

export function parseSurveyConditionalAnswerValue(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export const isCondition = (condition: ConditionalCondition | ConditionalCommand): condition is ConditionalCondition =>
  condition !== 'AND' && condition !== 'OR';

export const getConditionTriggerIds = (conditions: (ConditionalCondition | ConditionalCommand)[] | null | undefined): string[] =>
  (conditions ?? []).filter(isCondition).map((condition) => condition.questionId);

export const normalizeConditionGroups = (conditions: (ConditionalCondition | ConditionalCommand)[] = []): (ConditionalCondition | ConditionalCommand)[] =>
  conditions.map((condition) => {
    if (!isCondition(condition) || (condition.group !== undefined && condition.group !== null)) {
      return condition;
    }

    return {
      ...condition,
      group: 1
    };
  });

export function mapConditionGroups(conditions: (ConditionalCondition | ConditionalCommand)[] = []): ConditionGroup[] {
  if (conditions.length === 0) {
    return [];
  }

  const groupMap = new Map<number, ConditionGroup>();
  let group = isCondition(conditions[0]) ? (conditions[0].group ?? 1) : 1;

  conditions.forEach((condition) => {
    if (isCondition(condition) && condition.group !== group) {
      group = condition.group;
    }

    let entries = groupMap.get(group);
    if (!entries) {
      entries = [];
      groupMap.set(group, entries);
    }
    entries.push(condition);
  });

  return [...groupMap.values()];
}

export function mapConditionQueryParts(conditions: (ConditionalCondition | ConditionalCommand)[] = []): ConditionalCondition[][] {
  const conditionQueryParts: ConditionalCondition[][] = [];
  let groupIndex = 0;
  conditionQueryParts[groupIndex] = [];

  conditions.forEach((condition) => {
    if (isCondition(condition)) {
      conditionQueryParts[groupIndex].push(condition);
    } else if (condition === 'OR') {
      groupIndex++;
      conditionQueryParts[groupIndex] = [];
    }
  });

  return conditionQueryParts;
}

export function mapResolvedGroups(conditions: (boolean | ConditionalCommand)[] = []): boolean[][] {
  const conditionQueryParts: boolean[][] = [];
  let groupIndex = 0;
  conditionQueryParts[groupIndex] = [];

  conditions.forEach((condition) => {
    if (condition === 'OR') {
      groupIndex++;
      conditionQueryParts[groupIndex] = [];
    } else {
      conditionQueryParts[groupIndex].push(condition as boolean);
    }
  });

  return conditionQueryParts;
}

export function arraysHaveSameValues(first: string[], second: string[]): boolean {
  const firstSet = new Set(first);
  const secondSet = new Set(second);
  if (firstSet.size !== secondSet.size) {
    return false;
  }

  return [...firstSet].every((value) => secondSet.has(value));
}

function parseComparableNumber(value: ScalarConditionValue | string[]): number | null {
  if (Array.isArray(value)) {
    return null;
  }

  if (typeof value === 'string') {
    if (value.trim() === '') {
      return null;
    }
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function evaluateNumericCondition(operator: string, valueToMatch: string, valueToCheck: ScalarConditionValue | string[]): boolean {
  const matchedValue = parseComparableNumber(valueToMatch);
  const checkedValue = parseComparableNumber(valueToCheck);

  if (matchedValue === null || checkedValue === null) {
    return false;
  }

  switch (operator) {
    case 'LESS_THAN':
      return matchedValue > checkedValue;
    case 'LESS_THAN_OR_EQUAL':
      return matchedValue >= checkedValue;
    case 'GREATER_THAN':
      return matchedValue < checkedValue;
    case 'GREATER_THAN_OR_EQUAL':
      return matchedValue <= checkedValue;
    default:
      return false;
  }
}

function evaluateNpsScoreCondition(condition: ConditionalCondition, valueToCheck: ScalarConditionValue | string[]): boolean {
  const checkedValue = parseComparableNumber(valueToCheck);

  if (checkedValue === null) {
    return false;
  }

  const areaToMatch = checkedValue < 7 ? NPSAreasEnum.Detractors : checkedValue > 8 ? NPSAreasEnum.Promoters : NPSAreasEnum.Passives;
  return condition.valueToMatch.includes(areaToMatch);
}

export const mapItemsById = <T extends { id: string }>(items: T[] = []): Record<string, T> =>
  items.reduce(
    (mappedItems, item) => {
      mappedItems[item.id] = item;
      return mappedItems;
    },
    {} as Record<string, T>
  );

export function resolveConditionMatchValues(condition: ConditionalCondition, triggerQuestion?: ConditionalQuestionLike | null): ResolvedConditionMatchValues {
  const optionValueMap = new Map((triggerQuestion?.options ?? []).map((option) => [option.id, option.value ?? option.id]));
  const rankValueMap = new Map((triggerQuestion?.ranks ?? []).map((rank) => [rank.id, rank.value ?? rank.id]));

  const normalizedValueToMatch = (condition.valueToMatch ?? []).map((value) => rankValueMap.get(value) ?? optionValueMap.get(value) ?? value);
  const normalizedOptionId =
    condition.optionId === 'NONE' ? 'NONE' : condition.optionId ? (optionValueMap.get(condition.optionId) ?? condition.optionId) : undefined;

  return {
    normalizedOptionId,
    normalizedValueToMatch,
    plainValueToMatch: normalizedValueToMatch[0] ?? ''
  };
}

export function evaluateConditionOperator(
  condition: ConditionalCondition,
  valueToCheck: ScalarConditionValue | string[],
  triggerQuestion?: ConditionalQuestionLike | null,
  rawValues?: string[]
): boolean {
  const { plainValueToMatch, normalizedValueToMatch } = resolveConditionMatchValues(condition, triggerQuestion);

  if (['LESS_THAN', 'LESS_THAN_OR_EQUAL', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL'].includes(condition.operator)) {
    return evaluateNumericCondition(condition.operator, plainValueToMatch, valueToCheck);
  }

  if (condition.operator === 'NPS_SCORE') {
    return evaluateNpsScoreCondition(condition, valueToCheck);
  }

  if (typeof valueToCheck === 'number') {
    return false;
  }

  switch (condition.operator) {
    case 'EQUAL':
      return Array.isArray(valueToCheck) ? valueToCheck.length === 1 && plainValueToMatch === valueToCheck[0] : plainValueToMatch === valueToCheck;
    case 'NOT_EQUAL':
      return Array.isArray(valueToCheck) ? !(valueToCheck.length === 1 && plainValueToMatch === valueToCheck[0]) : plainValueToMatch !== valueToCheck;
    case 'EQUAL_ANY':
      return Array.isArray(valueToCheck) ? normalizedValueToMatch.some((value) => valueToCheck.includes(value)) : normalizedValueToMatch.includes(valueToCheck);
    case 'NOT_EMPTY':
      return valueToCheck?.length > 0;
    case 'INCLUDES_ALL':
      return normalizedValueToMatch.every((value) => valueToCheck?.includes(value));
    case 'INCLUDES_ANY':
      return normalizedValueToMatch.some((value) => valueToCheck?.includes(value));
    case 'NOT_INCLUDES':
      return !normalizedValueToMatch.some((value) => valueToCheck?.includes(value));
    case 'NOT_INCLUDES_ALL':
      return !normalizedValueToMatch.every((value) => valueToCheck?.includes(value));
    case 'CONTAINS': {
      if (Array.isArray(valueToCheck)) {
        return false;
      }
      return (rawValues ?? normalizedValueToMatch).every((value) => valueToCheck.trim().toLowerCase().includes(value.trim().toLowerCase()));
    }
    case 'NOT_CONTAINS': {
      if (Array.isArray(valueToCheck)) {
        return false;
      }
      return !(rawValues ?? normalizedValueToMatch).some((value) => valueToCheck.trim().toLowerCase().includes(value.trim().toLowerCase()));
    }
    case 'ONLY_IF':
      return arraysHaveSameValues(normalizedValueToMatch, Array.isArray(valueToCheck) ? valueToCheck : [valueToCheck]);
    default:
      return false;
  }
}

export function evaluateRankingCondition(condition: ConditionalCondition, valuesToCheck: string[], triggerQuestion?: ConditionalQuestionLike | null): boolean {
  const { normalizedOptionId, normalizedValueToMatch } = resolveConditionMatchValues(condition, triggerQuestion);
  const answerValues = valuesToCheck.map((value) => {
    const option = triggerQuestion?.options?.find((questionOption) => questionOption.id === value);
    return option?.value ?? value;
  });
  const valuePosition = answerValues.indexOf(normalizedOptionId) + 1;

  if (!normalizedOptionId || (['POSITION_BETWEEN', 'POSITION_NOT_BETWEEN'].includes(condition.operator) && normalizedValueToMatch.length !== 2)) {
    return false;
  }

  switch (condition.operator) {
    case 'POSITION_BETWEEN':
      return +normalizedValueToMatch[0] <= valuePosition && +normalizedValueToMatch[1] >= valuePosition;
    case 'POSITION_GREATER_THAN':
      return +normalizedValueToMatch[0] > valuePosition;
    case 'POSITION_GREATER_THAN_OR_EQUAL':
      return +normalizedValueToMatch[0] >= valuePosition;
    case 'EQUAL':
    case 'POSITION_EQUAL':
      return +normalizedValueToMatch[0] === valuePosition;
    case 'EQUAL_ANY':
    case 'POSITION_INCLUDES_ANY':
      return normalizedValueToMatch.some((value) => +value === valuePosition);
    case 'POSITION_NOT_BETWEEN':
      return !(+normalizedValueToMatch[0] <= valuePosition && +normalizedValueToMatch[1] >= valuePosition);
    case 'NOT_EQUAL':
    case 'POSITION_NOT_EQUAL':
      return +normalizedValueToMatch[0] !== valuePosition;
    case 'POSITION_SMALLER_THAN':
      return +normalizedValueToMatch[0] < +valuePosition;
    case 'POSITION_SMALLER_THAN_OR_EQUAL':
      return +normalizedValueToMatch[0] <= +valuePosition;
    case 'NOT_SELECTED':
      return !answerValues.includes(normalizedOptionId);
    case 'SELECTED':
      return answerValues.includes(normalizedOptionId);
    default:
      return false;
  }
}

export function getParsedAnswer(questionId: string, environment: SurveyConditionalEvaluationEnvironment): ParsedConditionalAnswerValue {
  if (environment.parsedAnswers && Object.prototype.hasOwnProperty.call(environment.parsedAnswers, questionId)) {
    return environment.parsedAnswers[questionId];
  }

  const parseAnswerValue = environment.parseAnswerValue ?? parseSurveyConditionalAnswerValue;
  const parsedAnswer = parseAnswerValue(environment.answers[questionId]) as ParsedConditionalAnswerValue;

  if (environment.parsedAnswers) {
    environment.parsedAnswers[questionId] = parsedAnswer;
  }

  return parsedAnswer;
}

export function evaluateCondition(condition: ConditionalCondition, environment: SurveyConditionalEvaluationEnvironment): boolean {
  const triggerQuestion = environment.questions[condition.questionId];
  const valueToCheck = getParsedAnswer(condition.questionId, environment);

  if (condition.cardId) {
    const cardValueToCheck = (valueToCheck as CarouselTypeResultLike | undefined)?.[condition.cardId]?.value as ScalarConditionValue | string[] | undefined;
    if (cardValueToCheck === undefined) {
      return false;
    }

    return evaluateConditionOperator(condition, cardValueToCheck, triggerQuestion);
  }

  if (condition.optionId) {
    const rankingAnswerValue = extractComparableValue(valueToCheck);
    return Array.isArray(rankingAnswerValue) ? evaluateRankingCondition(condition, rankingAnswerValue, triggerQuestion) : false;
  }

  const answerValue = extractComparableValue(valueToCheck);
  if (answerValue === undefined) {
    return false;
  }

  return evaluateConditionOperator(condition, answerValue, triggerQuestion, condition.valueToMatch);
}

export function evaluateConditionSet(
  conditions: (ConditionalCondition | ConditionalCommand)[] = [],
  environment: SurveyConditionalEvaluationEnvironment,
  evaluate: typeof evaluateCondition = evaluateCondition
): boolean {
  if (!conditions || conditions.length === 0) {
    return false;
  }

  const normalizedConditions = normalizeConditionGroups(conditions);
  const groupedConditions = mapConditionGroups(normalizedConditions);
  const resolvedConditionsMap: (boolean | ConditionalCommand)[] = [];

  groupedConditions.forEach((group) => {
    const queryParts = mapConditionQueryParts(group);
    const resolution = queryParts.some((queryPart) => queryPart.every((condition) => evaluate(condition, environment)));

    resolvedConditionsMap.push(resolution);
    const lastGroupEntry = group[group.length - 1];
    if (lastGroupEntry && !isCondition(lastGroupEntry)) {
      resolvedConditionsMap.push(lastGroupEntry);
    }
  });

  const resolvedParts = mapResolvedGroups(resolvedConditionsMap);
  return resolvedParts.some((part) => part.every((condition) => condition));
}

function extractComparableValue(valueToCheck: ParsedConditionalAnswerValue): ScalarConditionValue | string[] | undefined {
  if (valueToCheck === undefined || valueToCheck === null) {
    return undefined;
  }

  if (Array.isArray(valueToCheck)) {
    return valueToCheck;
  }

  if (typeof valueToCheck === 'object') {
    if ('value' in valueToCheck) {
      return (valueToCheck as ChoiceTypeResultLike).value;
    }

    return undefined;
  }

  return valueToCheck;
}
